/**
 * The Internal Audits record, `/record/AUD-<id>` (13-32): the record page resolves the `AUD` prefix here, since the
 * list is Oversight's and not one of the five registers (FIELDSET §3.6).
 *
 * - **Who:** Oversight only (the list's author, §2.6). Anyone else is told so, calmly, with a way back.
 * - **Which audit:** a live `AUD-12` is read through `useAudit` (the item, then its Internal Audits Audit Log rows);
 *   a practice `AUD-P3` opens only in practice mode (D-13-23-1).
 * - **Read:** the head (the audit name, ref, status, business unit, kind, due `D MMM YYYY`, amber once overdue,
 *   "From Centro" on a carried row); the columns by §3.6 stage; the trail in plain words (`SiblingTimeline`).
 * - **Act:** the registry's `audit` slot (`AUDIT_PANEL`), every write through the runtime's Writer.
 */

import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { PageContainer } from '../../components/layout';
import { Badge, Button, Spinner } from '../../components/ui';
import SiblingTimeline from '../../components/record/SiblingTimeline';
import Lines from '../../components/record/Lines';
import { AUDIT_PANEL } from '../../components/record/panels/registry';
import type { AuditSaved } from '../../components/record/panels/AuditPanel';
import { useActor } from '../../hooks/useActor';
import { useAudit } from '../../hooks/useAudits';
import { AUDIT_STAGES, AUDIT_STATUS, auditField, isCarried } from '../../config/fieldset/internalAudits';
import { AUDIT_VERB_WORDS, isOverdue } from '../../lib/auditVerbs';
import type { AuditRow } from '../../lib/auditVerbs';
import { auditEditFormUrl, readAuditRef } from '../../lib/auditQuery';
import { formatDay } from '../../lib/fieldValues';
import { NOT_RECORDED } from '../../lib/verbForm';
import cardStyles from '../../components/record/record.module.css';
import styles from './RecordPage.module.css';

function statusTone(status: string): 'success' | 'warning' | 'info' | 'neutral' {
  if (status === AUDIT_STATUS.approved) return 'success';
  if (status === AUDIT_STATUS.completed || status === AUDIT_STATUS.inProgress) return 'info';
  return 'neutral';
}

function Elsewhere({ title, children }: { title: string; children: ReactNode }) {
  return (
    <PageContainer maxWidth="md">
      <h1 className={styles.title}>{title}</h1>
      <div className={styles.lede}>{children}</div>
      <p className={styles.lede}>
        <Link to="/audits">Go to the audit list</Link> or back to <Link to="/">Today</Link>.
      </p>
    </PageContainer>
  );
}

export default function AuditRecordView() {
  const raw = useParams().ref ?? '';
  const parsed = readAuditRef(raw);
  const actorState = useActor();
  const actor = actorState.actor;
  const audit = useAudit(parsed?.ref ?? null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const reload = audit.reload;
  const onSaved = useCallback(
    (s: AuditSaved) => {
      const words = AUDIT_VERB_WORDS[s.verb] ?? 'The change';
      setSavedNote(
        s.auditPending > 0
          ? `${words}; ${s.auditPending} audit ${s.auditPending === 1 ? 'row' : 'rows'} will be posted.`
          : s.mode === 'practice'
            ? `${words} in practice mode. Nothing was written to SharePoint.`
            : `${words}.`,
      );
      reload();
    },
    [reload],
  );

  if (!parsed) return <Navigate to="/audits" replace />;
  if (parsed.ref !== raw) return <Navigate to={`/record/${parsed.ref}`} replace />;
  if (actor && !actor.roles.includes('Oversight')) {
    return (
      <Elsewhere title="Internal audits are Oversight's">
        <p>
          <strong>{parsed.ref}</strong> is an internal audit. They are scheduled, completed and approved by Oversight, so they
          open only for Oversight.
        </p>
      </Elsewhere>
    );
  }
  if (audit.status === 'practice-only') {
    return (
      <Elsewhere title="That's a practice audit">
        <p>
          <strong>{parsed.ref}</strong> was made in practice mode. Practice audits stay in the browser they were made in, and open
          only while practice mode is on.
        </p>
      </Elsewhere>
    );
  }
  if (audit.status === 'loading' || (audit.status === 'ready' && !actor)) {
    return (
      <PageContainer>
        <div className={styles.loading} role="status" aria-live="polite">
          <Spinner size="lg" />
          <span>Opening {parsed.ref}…</span>
        </div>
      </PageContainer>
    );
  }
  if (audit.status === 'not-found') {
    return (
      <Elsewhere title={`${parsed.ref} isn't on the audit list`}>
        <p>There&apos;s no internal audit with that reference. It may have been deleted, or the number may be mistyped.</p>
      </Elsewhere>
    );
  }
  if (audit.status !== 'ready' || !audit.row || !actor) {
    return (
      <PageContainer maxWidth="md">
        <h1 className={styles.title}>{parsed.ref}</h1>
        <p className={styles.failure} role="alert">
          Couldn&apos;t read this audit from the Quality &amp; Safety site{audit.error ? `: ${audit.error}` : '.'}
        </p>
        <Button variant="secondary" onClick={audit.reload}>
          Try again
        </Button>
      </PageContainer>
    );
  }

  const row = audit.row;
  const Panel = AUDIT_PANEL.component;
  return (
    <PageContainer>
      <AuditHead row={row} practice={parsed.practice} refreshing={audit.refreshing} savedNote={savedNote} />
      <div className={styles.layout}>
        <section className={styles.act} aria-labelledby="audit-act">
          <h2 id="audit-act" className={styles.areaTitle}>
            Work this audit
          </h2>
          <div className={cardStyles.panels}>
            <div className={cardStyles.panel} id={`panel-${AUDIT_PANEL.key}`}>
              <div className={cardStyles.panelHead}>
                <h3 className={cardStyles.panelTitle}>{AUDIT_PANEL.title}</h3>
              </div>
              <Panel
                key={`${row.ref}|${row.eTag}`}
                row={row}
                siblings={audit.siblings}
                actor={actor}
                editFormUrl={auditEditFormUrl(row.ref)}
                onSaved={onSaved}
                onReload={audit.reload}
              />
            </div>
          </div>
        </section>
        <section className={styles.read} aria-labelledby="audit-read">
          <h2 id="audit-read" className={styles.areaTitle}>
            The audit
          </h2>
          <AuditFields row={row} />
        </section>
        <section className={styles.trail} aria-labelledby="audit-trail">
          <h2 id="audit-trail" className={styles.areaTitle}>
            What happened, newest first
          </h2>
          <div className={cardStyles.section}>
            <SiblingTimeline register="internalAudits" rows={audit.siblings} actor={actor} />
          </div>
        </section>
      </div>
    </PageContainer>
  );
}

function AuditHead({ row, practice, refreshing, savedNote }: { row: AuditRow; practice: boolean; refreshing: boolean; savedNote: string | null }) {
  const f = row.fields;
  const status = typeof f.AuditStatus === 'string' ? f.AuditStatus : '';
  const title = typeof f.Title === 'string' && f.Title ? f.Title : row.ref;
  const overdue = isOverdue(row, new Date());
  const editUrl = auditEditFormUrl(row.ref);
  return (
    <header className={styles.head}>
      <p className={styles.kicker}>
        Internal audit · <span className={styles.ref}>{row.ref}</span>
        {practice && <span className={styles.practiceTag}>Practice audit</span>}
      </p>
      <h1 className={styles.title}>{title}</h1>
      <div className={styles.chips}>
        {status && (
          <Badge variant={statusTone(status)} size="sm">
            {status}
          </Badge>
        )}
        {typeof f.BusinessUnit === 'string' && f.BusinessUnit && <span className={styles.chip}>{f.BusinessUnit}</span>}
        {typeof f.AuditKind === 'string' && f.AuditKind && <span className={styles.chip}>{f.AuditKind}</span>}
        {typeof f.DueOn === 'string' && f.DueOn && (
          <span className={`${styles.chip} ${overdue ? styles.chipTodo : ''}`}>
            Due {formatDay(f.DueOn)}
            {overdue ? ' · overdue' : ''}
          </span>
        )}
        {isCarried(f) && <span className={styles.chip}>From Centro</span>}
        {editUrl ? (
          <a className={styles.chipLink} href={editUrl} target="_blank" rel="noreferrer">
            Working papers: open on SharePoint ↗
          </a>
        ) : (
          <span className={styles.chip}>Working papers: none on a practice audit</span>
        )}
        {refreshing && <span className={styles.refreshing}>Refreshing…</span>}
      </div>
      {savedNote && (
        <p className={styles.savedNote} role="status">
          {savedNote}
        </p>
      )}
    </header>
  );
}

const SECTIONS: ReadonlyArray<{ id: string; title: string; columns: readonly string[] }> = [
  { id: 'schedule', title: 'The schedule', columns: AUDIT_STAGES['lodge-oversight'].filter((c) => c !== 'Title' && c !== 'AuditStatus') },
  { id: 'completion', title: 'Completion', columns: AUDIT_STAGES.investigation.filter((c) => c !== 'AuditStatus' && c !== 'Attachments') },
  { id: 'approval', title: 'Approval', columns: AUDIT_STAGES.closure.filter((c) => c !== 'AuditStatus') },
];

function AuditFields({ row }: { row: AuditRow }) {
  const sections = useMemo(
    () => (isCarried(row.fields) ? [...SECTIONS, { id: 'centro', title: 'From Centro', columns: ['CentroRef'] }] : SECTIONS),
    [row.fields],
  );
  return (
    <div className={cardStyles.sections}>
      {sections.map((s) => (
        <section key={s.id} className={cardStyles.section} aria-labelledby={`af-${s.id}`}>
          <h3 id={`af-${s.id}`} className={cardStyles.sectionTitle}>
            {s.title}
          </h3>
          <dl className={cardStyles.facts}>
            {s.columns.map((c) => {
              const spec = auditField(c);
              const v = row.fields[c];
              const blank = v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
              let shown: ReactNode = <span className={cardStyles.blank}>{NOT_RECORDED}</span>;
              if (!blank) {
                const text = String(v);
                shown =
                  spec?.kind === 'day-new' ? (
                    formatDay(text)
                  ) : spec?.kind === 'number' ? (
                    `${text}%`
                  ) : spec?.kind === 'link' ? (
                    <a href={text} target="_blank" rel="noreferrer">
                      {text}
                    </a>
                  ) : spec?.kind === 'textarea' ? (
                    <Lines text={text} />
                  ) : (
                    text
                  );
              }
              return (
                <div key={c} style={{ display: 'contents' }}>
                  <dt>{spec?.label ?? c}</dt>
                  <dd className={spec?.kind === 'textarea' ? cardStyles.multiline : undefined}>{shown}</dd>
                </div>
              );
            })}
          </dl>
        </section>
      ))}
    </div>
  );
}
